import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';

export default function App() {
  const [displayName, setDisplayName] = useState('');
  const [userId, setUserId] = useState('');
  const [countdown, setCountdown] = useState(null);
  const [gameRoomId, setGameRoomId] = useState(null);
  const [isWaitingForNextGame, setIsWaitingForNextGame] = useState(false);
  const [leaderboard, setLeaderboard] = useState({ daily: [], weekly: [], monthly: [] });
  const [selectedPeriod, setSelectedPeriod] = useState('daily');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackStatus, setFeedbackStatus] = useState('');
  const [winningGoal, setWinningGoal] = useState(null);
  const [showPwaPrompt, setShowPwaPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const connectionRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const navigate = useNavigate();

  // Load or generate user ID on component mount
  useEffect(() => {
    const savedUserId = localStorage.getItem('gameUserId');
    const savedDisplayName = localStorage.getItem('gameDisplayName');
    
    if (savedUserId) {
      setUserId(savedUserId);
      if (savedDisplayName) {
        setDisplayName(savedDisplayName);
      }
    } else {
      // Generate new user ID through backend
      generateNewUserId();
    }
  }, []);

  // Fetch leaderboard data
  useEffect(() => {
    fetchLeaderboard();
    // Refresh leaderboard every 30 seconds
    const interval = setInterval(fetchLeaderboard, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch('https://api.paythefrog.io/api/leaderboard?topCount=10');
      if (response.ok) {
        const data = await response.json();
        setLeaderboard({
          daily: data.daily,
          weekly: data.weekly,
          monthly: data.monthly
        });
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    }
  };

  const generateNewUserId = async () => {
    try {
      const response = await fetch('https://api.paythefrog.io/api/users/generate-id', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserId(data.userId);
        setDisplayName(data.displayName);
        localStorage.setItem('gameUserId', data.userId);
        localStorage.setItem('gameDisplayName', data.displayName);
      }
    } catch (error) {
      console.error('Failed to generate user ID:', error);
    }
  };

  // Save display name to localStorage whenever it changes
  useEffect(() => {
    if (displayName.trim()) {
      localStorage.setItem('gameDisplayName', displayName);
    }
  }, [displayName]);

  // Setup SignalR connection for getting game room info
  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl('https://api.paythefrog.io/api/gameHub')
      .build();

    connectionRef.current = connection;

    connection.start().then(() => {
      console.log('Connected to SignalR hub');
      // Current room info will be sent automatically on connection
    }).catch(err => console.error('SignalR connection error:', err));

    // Handle current game room info
    connection.on('CurrentGameRoom', (data) => {
      setGameRoomId(data.gameRoomId);
      setCountdown(data.remainingSeconds);
      setWinningGoal(data.winningGoal); // Set winning goal
      
      // Start local countdown
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      
      let currentCountdown = data.remainingSeconds;
      countdownIntervalRef.current = setInterval(() => {
        currentCountdown = Math.max(0, currentCountdown - 1);
        setCountdown(currentCountdown);
        
        if (currentCountdown <= 0) {
          clearInterval(countdownIntervalRef.current);
        }
      }, 1000);
    });

    // Handle new room creation
    connection.on('NewRoomCreated', (data) => {
      setGameRoomId(data.gameRoomId);
      setCountdown(data.remainingSeconds);
      setWinningGoal(data.winningGoal); // Set winning goal
      
      // If user is waiting for next game, navigate immediately to new room
      if (isWaitingForNextGame) {
        handleNavigateToGame(data.gameRoomId, data.remainingSeconds);
        return;
      }
      
      // Start local countdown for new room
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      
      let currentCountdown = data.remainingSeconds;
      countdownIntervalRef.current = setInterval(() => {
        currentCountdown = Math.max(0, currentCountdown - 1);
        setCountdown(currentCountdown);
        
        if (currentCountdown <= 0) {
          clearInterval(countdownIntervalRef.current);
        }
      }, 1000);
    });

    connection.on('Error', (message) => {
      console.error('Game room error:', message);
    });
    
    // Handle game already started error
    connection.on('GameAlreadyStarted', (data) => {
      console.log('Game already started or ended:', data.message);
      alert(data.message);
      // User will stay on homepage
      setIsWaitingForNextGame(false);
    });

    // Handle feedback confirmation
    connection.on('FeedbackReceived', (message) => {
      setFeedbackStatus(message);
      setTimeout(() => {
        setIsFeedbackModalOpen(false);
        setFeedbackStatus('');
        setFeedbackText('');
      }, 2000);
    });

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      connection.stop();
    };
  }, [isWaitingForNextGame]);

  const handleNavigateToGame = (roomId = gameRoomId, countdownTime = countdown) => {
    if (userId && displayName.trim()) {
      localStorage.setItem('gameDisplayName', displayName);
      // Clear any previously stored game room when explicitly starting new session
      localStorage.removeItem('currentGameRoomId');
      // Navigate to game page with userId and displayName
      navigate('/game', { 
        state: { 
          userId: userId,
          displayName: displayName, 
          gameRoomId: roomId,
          countdown: countdownTime 
        } 
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!userId) return;
    
    if (isWaitingForNextGame) {
      // Cancel waiting
      setIsWaitingForNextGame(false);
    } else if (displayName.trim()) {
      localStorage.setItem('gameDisplayName', displayName);
      
      // If countdown is greater than 0, wait for next game
      if (countdown > 0) {
        setIsWaitingForNextGame(true);
      }
    }
  };

  const handleSendFeedback = (e) => {
    e.preventDefault();
    if (!feedbackText.trim() || !connectionRef.current) return;
    
    connectionRef.current.invoke('SendFeedback', userId, feedbackText)
      .catch(err => {
        console.error('Error sending feedback:', err);
        setFeedbackStatus('Failed to send feedback.');
      });
  };

  const getCurrentLeaderboard = () => {
    switch (selectedPeriod) {
      case 'daily':
        return leaderboard.daily;
      case 'weekly':
        return leaderboard.weekly;
      case 'monthly':
        return leaderboard.monthly;
      default:
        return [];
    }
  };

  const getMedalEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `${rank}.`;
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount}`;
  };

  // Detect mobile device and PWA status
  useEffect(() => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                               window.navigator.standalone || 
                               document.referrer.includes('android-app://');
    
    if (isMobile && !isInStandaloneMode) {
      setShowPwaPrompt(true);
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleDismissPwaPrompt = () => {
    setShowPwaPrompt(false);
  };

  const handleInstallPwa = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPwaPrompt(false);
      }
      setDeferredPrompt(null);
    } else {
      // Fallback for browsers that don't support beforeinstallprompt
      // or iOS devices - show instructions
      alert('To install this app:\n\niOS: Tap the Share button and select "Add to Home Screen"\n\nAndroid: Tap the menu (⋮) and select "Add to Home screen" or "Install app"');
    }
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: 0,
      padding: '2rem',
      gap: '2rem',
      flexWrap: 'wrap',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      animation: 'fadeIn 1s ease-in-out'
    }}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes slideDown {
            from { transform: translateY(-100%); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          @media (max-width: 768px) {
            .table-layout { flex-direction: column; }
          }
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }
          .modal-content {
            background: white;
            border-radius: 15px;
            padding: 2rem;
            max-width: 500px;
            width: 90%;
            max-height: 80vh;
            overflow-y: auto;
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
            animation: fadeIn 0.3s ease-in-out;
          }
          .modal-close {
            position: absolute;
            top: 10px;
            right: 15px;
            background: none;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            color: #666;
          }
          .modal-close:hover {
            color: #333;
          }
          .pwa-banner {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            background: linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%);
            color: white;
            padding: 1rem;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
            z-index: 999;
            animation: slideDown 0.5s ease-out;
          }
        `}
      </style>

      {/* PWA Installation Banner */}
      {showPwaPrompt && (
        <div className="pwa-banner">
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                📱 Install Pay the Frog
              </div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                Install our app for a better mobile experience!
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleInstallPwa}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'white',
                  color: '#2E7D32',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                Install
              </button>
              <button
                onClick={handleDismissPwaPrompt}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '1px solid white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Big Table Layout */}
      <div className="table-layout" style={{
        display: 'flex',
        backgroundColor: 'white',
        borderRadius: '15px',
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
        minHeight: '600px',
        width: '100%',
        maxWidth: '1200px',
        overflow: 'hidden',
        transition: 'transform 0.3s ease',
        marginTop: showPwaPrompt ? '80px' : '0'
      }}>
        {/* Left Column: Leaderboard */}
        <div style={{
          flex: 1,
          padding: '2rem',
          borderRight: '2px solid #e0e0e0',
          background: 'linear-gradient(to bottom, #f9f9f9 0%, #ffffff 100%)'
        }}>
          <h2 style={{ 
            marginBottom: '1rem', 
            color: '#2E7D32', 
            textAlign: 'center',
            fontSize: '1.8rem',
            fontWeight: 'bold',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
          }}>
            🏆 Leaderboard
          </h2>
          
          {/* Period Selector */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '1rem',
            justifyContent: 'center'
          }}>
            {['daily', 'weekly', 'monthly'].map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.9rem',
                  backgroundColor: selectedPeriod === period ? '#4CAF50' : '#f0f0f0',
                  color: selectedPeriod === period ? 'white' : '#666',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  transition: 'all 0.3s ease',
                  boxShadow: selectedPeriod === period ? '0 4px 8px rgba(76, 175, 80, 0.3)' : 'none',
                  transform: selectedPeriod === period ? 'scale(1.05)' : 'scale(1)'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.target.style.transform = selectedPeriod === period ? 'scale(1.05)' : 'scale(1)'}
              >
                {period}
              </button>
            ))}
          </div>

          {/* Leaderboard List */}
          <div style={{
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            {getCurrentLeaderboard().length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#999',
                fontStyle: 'italic'
              }}>
                No winners yet this {selectedPeriod.replace('ly', '')}!
              </div>
            ) : (
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {getCurrentLeaderboard().map((entry) => (
                  <li
                    key={entry.displayName}
                    style={{
                      padding: '0.75rem',
                      marginBottom: '0.5rem',
                      backgroundColor: entry.rank <= 3 ? '#FFF9C4' : '#f9f9f9',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'background-color 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1.2rem', minWidth: '30px' }}>
                        {getMedalEmoji(entry.rank)}
                      </span>
                      <span style={{ 
                        fontWeight: 'normal',
                        color: '#333'
                      }}>
                        {entry.displayName}
                      </span>
                    </div>
                    <span style={{
                      fontWeight: 'bold',
                      color: '#4CAF50',
                      fontSize: '1.1rem'
                    }}>
                      {entry.wins} {entry.wins === 1 ? 'win' : 'wins'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Middle Column: Game Joining */}
        <div style={{
          flex: 2,
          padding: '2rem',
          textAlign: 'center',
          borderRight: '2px solid #e0e0e0',
          background: 'linear-gradient(to bottom, #ffffff 0%, #f5f5f5 100%)'
        }}>
          <h1 style={{ 
            marginBottom: '0.5rem', 
            color: '#2E7D32',
            fontSize: '2.5rem',
            fontWeight: 'bold',
            textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
          }}>
            Pay the Frog
          </h1>
          <p style={{ 
            marginBottom: '1rem', 
            color: '#666',
            fontSize: '0.8rem',
            fontWeight: 'bold'
          }}>
            v0.4
          </p>
          
          <div style={{ 
            marginBottom: '1rem', 
            padding: '0.75rem', 
            backgroundColor: '#e8f5e8', 
            borderRadius: '10px',
            color: '#666',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '1.1em', fontWeight: 'bold', color: '#2E7D32', marginBottom: '0.5rem' }}>
              Next Game Goal: {winningGoal !== null ? formatCurrency(winningGoal) : 'Loading...'}
            </div>
            <div style={{ fontSize: '0.9em', color: '#888' }}>
              New room in: {countdown !== null ? `${countdown}s` : 'Loading...'}
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', width: '100%' }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Enter your display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={isWaitingForNextGame}
                maxLength={16}
                style={{
                  padding: '0.75rem',
                  fontSize: '1rem',
                  border: '2px solid #ddd',
                  borderRadius: '10px',
                  marginBottom: '1rem',
                  width: '250px',
                  outline: 'none',
                  backgroundColor: isWaitingForNextGame ? '#f5f5f5' : 'white',
                  color: isWaitingForNextGame ? '#999' : 'black',
                  transition: 'border-color 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
              <button
                type="submit"
                disabled={!userId}
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  backgroundColor: isWaitingForNextGame ? '#f44336' : '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: !userId ? 'not-allowed' : 'pointer',
                  opacity: !userId ? 0.8 : 1,
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  transform: 'scale(1)',
                  width: '150px'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'
                }
              >
                {isWaitingForNextGame ? 'Cancel Waiting' : 'Enter Game'}
              </button>
            </form>
            <button
              onClick={() => setIsModalOpen(true)} // Updated to open modal
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                transform: 'scale(1)',
                width: '150px'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'
              }
            >
              How to Play
            </button>

            <button
              onClick={() => setIsFeedbackModalOpen(true)}
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                backgroundColor: '#FF9800',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                transform: 'scale(1)',
                width: '150px'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'
              }
            >
              Feedback
            </button>
          </div>
        </div>

        {/* Right Column: Patch Notes */}
        <div style={{
          flex: 1,
          padding: '2rem',
          background: 'linear-gradient(to bottom, #f9f9f9 0%, #ffffff 100%)'
        }}>
          <h2 style={{ 
            marginBottom: '1rem', 
            color: '#2E7D32', 
            textAlign: 'center',
            fontSize: '1.8rem',
            fontWeight: 'bold',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
          }}>
            📋 Patch Notes
          </h2>
          <div style={{
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '1rem',
            backgroundColor: '#f9f9f9',
            borderRadius: '10px',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <p style={{ margin: '0 0 1rem 0', color: '#666' }}>
              <strong>Release Version v0.4</strong><br />
              - Added actions to sabotage other players<br />
              - Improved UI
            </p>
            <p style={{ margin: '0 0 1rem 0', color: '#666' }}>
              <strong>Release Version v0.3</strong><br />
              - Added property list button<br />
              - General UI fixes and improvements
            </p>
            <p style={{ margin: '0 0 1rem 0', color: '#666' }}>
              <strong>Release Version v0.2</strong><br />
              - Bug fixes<br />
              - Goal displayed in the home page<br />
              - Feedback button<br />
              - Enhanced van/ship animations<br />
              - Income and operating costs adjustments
            </p>
            <p style={{ margin: '0 0 1rem 0', color: '#666' }}>
              <strong>Release Version v0.1</strong>
            </p>
          </div>
        </div>
      </div>
      
      {/* Modal for How to Play */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.target === e.currentTarget || e.stopPropagation()}>
            <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            <h2 style={{ color: '#2E7D32', marginBottom: '1rem', textAlign: 'center' }}>🐸 How to Play Pay the Frog</h2>
            <div style={{ color: '#333', lineHeight: '1.6' }}>
              <p><strong>Welcome!</strong> Pay the Frog is a multiplayer competitive economy game where you build and manage your empire to reach the winning goal.</p>
              
              <h3>🎯 Objective</h3>
              <p>Be the first player to reach the winning value (e.g., $500,000) through smart economic decisions. The game ends when someone hits the goal or time runs out, where the player with the best value wins.</p>
              
              <h3>🏠 Getting Started</h3>
              <p>- Enter your display name and join the game when a room is available.<br/>
              - You'll start with $50,000.</p>
              
              <h3>🏛️ Key Mechanics</h3>
              <ul>
                <li><strong>Auctions:</strong> Bid on properties like farms, mines, and factories to expand your empire.</li>
                <li><strong>Routes:</strong> Connect properties (e.g., farm to market) to generate income from shipping or land transport.</li>
                <li><strong>Banks:</strong> Deposit money to earn interest from borrowers or take loans to grow faster.</li>
                <li><strong>Upgrades:</strong> Improve your properties to increase income and efficiency.</li>
                <li><strong>Trading:</strong> Form agreements with other players for mutual benefits.</li>
                <li><strong>Stocks:</strong> Buy shares in other players' empires for profit or to take them over.</li>
              </ul>
              
              <h3>🐸 Prog</h3>
              <p>A mischievous frog, named Prog, randomly visits industries (he prefers "dirty" industries), greatly increasing operating costs temporarily. Watch out and manage your expenses!</p>
              
              <h3>💡 Tips</h3>
              <p>- Balance income and costs to avoid bankruptcy.<br/>
              - Collaborate or compete strategically.<br/>
              - Keep an eye on the economy status for bonuses or penalties.</p>
              
              <p style={{ textAlign: 'center', marginTop: '1rem', fontStyle: 'italic' }}>Good luck, and may the best win! 🏆</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Feedback */}
      {isFeedbackModalOpen && (
        <div className="modal-overlay" onClick={() => setIsFeedbackModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.target === e.currentTarget || e.stopPropagation()}>
            <button className="modal-close" onClick={() => setIsFeedbackModalOpen(false)}>×</button>
            <h2 style={{ color: '#FF9800', marginBottom: '1rem', textAlign: 'center' }}>📝 Send Feedback</h2>
            
            {feedbackStatus ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#4CAF50', fontSize: '1.2rem' }}>
                {feedbackStatus}
              </div>
            ) : (
              <form onSubmit={handleSendFeedback} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <p style={{ color: '#666', textAlign: 'center' }}>
                  Found a bug? Have a suggestion? Let us know!
                </p>
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Type your feedback here..."
                  rows={5}
                  style={{
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                  required
                />
                <button
                  type="submit"
                  style={{
                    padding: '0.75rem',
                    backgroundColor: '#FF9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  Submit Feedback
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
